import React, { useRef } from "react";
import PropTypes from "prop-types";
import { Pie } from "react-chartjs-2";
import formatNumber from "../../../utils/formatNumber";
import "chartjs-plugin-datalabels";
import numbro from "numbro";
import { useLightTheme } from "../../../hooks/useLightTheme";

const tooltipWidth = 300;

const PieChart = (props) => {
  const isLight = useLightTheme();

  const {
    data: chartValues = [],
    currency = "",
    addOtherLabel = true,
    isSortable = true,
    maxSlices = 8,
    customTooltip = false,
    responsive = true,
    tooltipFormatter,
    hideTooltipTitle = false,
    legendLabelMaxLength = 20,
    width,
    height,
    showLegend,
    legendPosition,
    isLegendTooltip,
    isClubPilates = false,
    backgroundColorClubPilates = [
      "#5c8cc9",
      "#87bec3",
      "#54a5ac",
      "#85959f",
      "#dadfe2",
      "#a7c0e2",
      "#b3d7db",
      "#5b676e",
      ""
    ],
    backgroundColor = [
      "#ff664e",
      "#1794ca",
      "#21e981",
      "#df30e5",
      "#1cdac4",
      "#7d4eff",
      "#ffbe4e",
      "#ca5317",
      "red"
    ],
    hoverBackgroundColorClubPilates = [],
    hoverBackgroundColor = [
      "#ed5740",
      "#0084b8",
      "#00d872",
      "#cc0ad3",
      "#00c8b2",
      "#6a3eed",
      "#efae3e",
      "#b74305",
      "red"
    ],
    legendTooltipPosition = "right"
  } = props;

  const tooltipRef = useRef();
  const showLegendLabelTooltip =
    showLegend && isLegendTooltip && (legendPosition === "right" || legendPosition === "left");

  const labels = [];
  let data = [];
  const indexForOther = maxSlices - 1;
  let allZerosOrEmpty = true;

  const onHover = (legendToolTips, legendTooltipPosition) => {
    return (event, legendItem) => {
      let tooltip = tooltipRef;
      let { offsetX, offsetY } = event;
      if (tooltip && legendToolTips[legendItem.index].length > 20) {
        let offsetFromCursor = legendTooltipPosition === "left" ? -(tooltipWidth + 20) : 20;

        tooltip.current.innerHTML = legendToolTips[legendItem.index];
        tooltip.current.style.left = offsetX + offsetFromCursor + "px";
        tooltip.current.style.top = offsetY + "px";
        tooltip.current.style.padding = "6px";
      }
    };
  };

  const onLeave = () => {
    let tooltip = tooltipRef;

    if (tooltip) {
      tooltip.current.style.padding = "0";
      tooltip.current.innerHTML = "";
    }
  };

  const truncateLegendLabel = (item) => {
    if (!item.text) return false;
    item.text =
      item.text.length > legendLabelMaxLength
        ? item.text.slice(0, legendLabelMaxLength) + "..."
        : item.text;
    return true;
  };

  const handleClick = (event) => {
    if (props.clickablePie) {
      let index = event[0]["_index"];
      window.open(props.data[index][2]);
    }
  };

  const renderPlugins = (data, allZerosOrEmpty) => {
    let labels = { datalabels: { display: false } };

    if (allZerosOrEmpty) return labels;
    if (props.showRawLabels) return { datalabels: { display: true } };

    if (props.showPercent) {
      labels = {
        datalabels: {
          formatter: (value) => {
            let sum = 0;
            data.map((data) => (sum += data));
            let percentage = ((value * 100) / sum).toFixed(2);
            return percentage > 10 ? percentage + "%" : "";
          },
          color: "#43474e",
          fontWeight: "800"
        }
      };
    }

    return labels;
  };

  if (isSortable) {
    chartValues.sort((a, b) => {
      let num1 = numbro.unformat(a[1]);
      let num2 = numbro.unformat(b[1]);
      // by desc
      return num2 - num1;
    });
  }

  const populateLabelsAndDataArrays = (label, num) => {
    labels.push(label);
    data.push(num);
  };

  const handleOtherLabelAndItsData = (num, index) => {
    data[indexForOther] += num;
    chartValues.length - 1 === index ? (labels[indexForOther] = "Other") : null;
  };

  Array.isArray(chartValues) &&
    chartValues.forEach((subArray, index) => {
      //converts string to number
      let num = numbro.unformat(subArray[1]);
      allZerosOrEmpty = allZerosOrEmpty && num === 0;
      let label = subArray[0];

      if (addOtherLabel) {
        index <= indexForOther
          ? populateLabelsAndDataArrays(label, num)
          : handleOtherLabelAndItsData(num, index);
      } else populateLabelsAndDataArrays(label, num);
    });

  if (allZerosOrEmpty) {
    data = Array.from(
      {
        length:
          chartValues.length && chartValues.length < maxSlices ? chartValues.length : maxSlices
      },
      () => 1
    );
  }

  if (chartValues) {
    const legendTooltipStyle = {
      width: tooltipWidth + "px",
      textAlign: "center",
      backgroundColor: isLight ? "#fff" : "#000",
      color: isLight ? "#717173" : "#fff",
      position: "absolute",
      zIndex: "1",
      borderRadius: "6px",
      fontSize: "13px"
    };

    return (
      <>
        <Pie
          width={width}
          height={height}
          getElementsAtEvent={(e) => handleClick(e)}
          data={{
            labels,
            datasets: [
              {
                data,
                backgroundColor: isClubPilates ? backgroundColorClubPilates : backgroundColor,
                hoverBackgroundColor: isClubPilates
                  ? hoverBackgroundColorClubPilates
                  : hoverBackgroundColor,
                borderWidth: 1,
                borderColor: "#5f6566"
              }
            ]
          }}
          options={{
            maintainAspectRatio: false,
            responsive,
            plugins: renderPlugins(data, allZerosOrEmpty),
            tooltips: {
              backgroundColor: isLight ? "#fff" : "rgba(0, 0, 0, 0.8)",
              titleFontColor: isLight ? "#1f1f20" : "#fff",
              bodyFontColor: isLight ? "#717173" : "#fff",
              titleFontStyle: "bold",
              borderColor: isLight ? "#e6e7e8" : "transparent",
              borderWidth: 1,
              callbacks: {
                label: (tooltipItem, chartData) => {
                  if (allZerosOrEmpty) return "";
                  const index = tooltipItem.index;

                  if (tooltipFormatter) {
                    return tooltipFormatter({
                      index,
                      labels,
                      chartValues,
                      currency
                    });
                  }

                  const dataset = chartData.datasets[tooltipItem.datasetIndex];
                  const currentValue = dataset.data[index];

                  return !customTooltip
                    ? `${currency}${formatNumber(currentValue, { thousandSeparated: true })}`
                    : chartValues[index][2];
                },
                title: (tooltipItem, data) =>
                  allZerosOrEmpty || hideTooltipTitle ? "" : data.labels[tooltipItem[0].index]
              }
            },
            legend: {
              display: showLegend,
              position: legendPosition || "bottom",
              onHover: showLegendLabelTooltip && onHover(labels, legendTooltipPosition),
              onLeave: showLegendLabelTooltip && onLeave,
              labels: {
                fontColor: "#87919f",
                fontStyle: "bold",
                filter: showLegendLabelTooltip && truncateLegendLabel
              }
            }
          }}
        />
        <div id="tooltip" style={legendTooltipStyle} ref={tooltipRef} />
      </>
    );
  }
};

PieChart.propTypes = {
  data: PropTypes.array.isRequired,
  showLegend: PropTypes.bool,
  legendPosition: PropTypes.string,
  clickablePie: PropTypes.bool,
  backgroundColor: PropTypes.array,
  hoverBackgroundColor: PropTypes.array,
  isClubPilates: PropTypes.bool,
  backgroundColorClubPilates: PropTypes.array,
  hoverBackgroundColorClubPilates: PropTypes.array,
  showPercent: PropTypes.bool,
  currency: PropTypes.string,
  addOtherLabel: PropTypes.bool,
  maxSlices: PropTypes.number,
  isSortable: PropTypes.bool,
  customTooltip: PropTypes.bool,
  responsive: PropTypes.bool,
  isLegendTooltip: PropTypes.bool,
  width: PropTypes.number,
  height: PropTypes.number
};

export default PieChart;
